import React, { PureComponent } from 'react'
import ReactDOM from 'react-dom'
import { translate } from 'react-polyglot'
import TextareaAutosize from 'react-textarea-autosize'
import Mousetrap from 'mousetrap'

// import PlaceholderPlugin from 'slate-react-placeholder'
import { Editor as SlateEditor } from 'slate-react'
import Plain from 'slate-plain-serializer'
import InsertImages from 'slate-drop-or-paste-images'
import { getShortcutString } from 'components/Shortcut'
import SuggestionsPlugin from './Plugins/Suggestions'
import plugins from './Plugins'
import commands from './Commands'
import schema from './Schema'
import queries from './Queries'
import renderBlock from './Renderer/renderBlock'
import renderInline from './Renderer/renderInline'
import renderMark from './Renderer/renderMark'

import MetaData from './Components/MetaData'
import StatusInfo from './Components/StatusInfo'

import InlineToolbar from './Components/Toolbars/Inline'
import {
	InsertsToolbar,
	updateInsertsToolbar,
	updateInsertsToolbarPosition,
} from './Components/Toolbars/Inserts'
import EmbedInput from './Components/Toolbars/EmbedInput'
import { quickCommandList, quickCommandActions } from './quickCommands'

import './entry.scss'
import './editor.scss'

class Editor extends PureComponent {
	suggestionsPlugin = SuggestionsPlugin({
		trigger: '/',
		capture: /\/([a-zA-ZäöüÄÖÜß]*)/,
		startOfParagraph: true,
		suggestions: quickCommandList,
		onEnter: suggestion => {
			const editor = this.editor.current
			const {
				anchorText,
				selection: {
					anchor: { offset },
				},
			} = editor.value

			if (
				suggestion.value === 'setVimeo' ||
				suggestion.value === 'setYoutube' ||
				suggestion.value === 'setDraw' ||
				suggestion.value === 'setFigma' ||
				suggestion.value === 'setGist' ||
				suggestion.value === 'setCodepen'
			) {
				this.setState(
					{
						suggestionType: suggestion.value,
						embedInputVisible: true,
					},
					() => {
						editor
							.deleteBackward(offset)
							.deleteForward(anchorText.text.length - offset)
					}
				)
			} else {
				editor
					.deleteBackward(offset)
					.deleteForward(anchorText.text.length - offset)

				quickCommandActions(editor, suggestion)
			}
		},
	})

	plugins = [
		// PlaceholderPlugin({
		// 	placeholder: 'Type your text here or use "/" for quick commands 👋',
		// 	when: 'isEmpty',
		// }),
		this.suggestionsPlugin,
		...plugins,
		InsertImages({
			insertImage: (editor, file) => {
				const { id } = this.props

				if (file.type) {
					editor.insertFile(file, id)
				} else {
					alert('An error occurred while uploading the image.')
				}
			},
		}),
	]

	constructor(props) {
		super(props)

		this.state = {
			editorHasFocus: false,
			embedInputVisible: false,
			suggestionType: null,
			rebuild: false,
		}

		this.timer = null

		this.editor = React.createRef()
		this.insertsToolbarRef = React.createRef()
		this.inlineToolbarRef = React.createRef()
		this.entryRef = React.createRef()

		this.ON_LEAVE_MESSAGE = props.t('unsaved_message')

		window.addEventListener('keydown', event => {
			const { isUnsaved, onSave } = this.props

			if (!(event.ctrlKey || event.metaKey) || event.key !== 's') {
				return
			}

			event.preventDefault()

			if (isUnsaved) {
				onSave()
			}
		})

		window.addEventListener('beforeunload', event => {
			const { isUnsaved } = this.props

			if (!isUnsaved) {
				return
			}

			const confirmationMessage = this.ON_LEAVE_MESSAGE

			event.returnValue = confirmationMessage // Gecko, Trident, Chrome 34+
			return confirmationMessage
		})
	}

	componentDidMount() {
		this.bindHotKey('setBold', () => {
			this.editor.current.toggleMark('bold')
		})
		this.bindHotKey('setItalic', () => {
			this.editor.current.toggleMark('italic')
		})
		this.bindHotKey('setStrikedThrough', () => {
			this.editor.current.toggleMark('strikethrough')
		})
		this.bindHotKey('setHighlight', () => {
			this.editor.current.toggleMark('important')
		})
		this.bindHotKey('setCode', () => {
			this.editor.current.toggleMark('code')
		})
		// this.bindHotKey('setH2', () => { this.editor.current.command('setH2') })
		// this.bindHotKey('setH3', () => { this.editor.current.command('setH3') })
		this.bindHotKey('setHr', () => {
			this.editor.current.insertBlock({ type: 'hr' })
		})
		this.bindHotKey('setParagraph', () => {
			this.editor.current.setBlocks('paragraph')
		})
		this.bindHotKey('uploadImage', () => {
			document.getElementById('image-input').click()
		})
		this.bindHotKey('uploadFile', () => {
			document.getElementById('file-input').click()
		})
		// this.bindHotKey('setBlockquote', () => { this.editor.current.command('italic') })
		// this.bindHotKey('setOrderedList', () => { this.editor.current.command('italic') })
		// this.bindHotKey('setUnorderedList', () => { this.editor.current.command('italic') })

		window.addEventListener('resize', this.onResize)

		this.setState({ rebuild: true })
	}

	componentWillUnmount() {
		// 	Mousetrap.unbind(getShortcutString('setBold'), this.editor.current.command('setH2'))
		window.removeEventListener('resize', this.onResize)

		clearTimeout(this.timer)
	}

	bindHotKey = (key, callback) => {
		Mousetrap(ReactDOM.findDOMNode(this.editor.current)).bind(
			getShortcutString(key),
			callback
		)
	}

	onResize = () => {
		const { value } = this.props

		updateInsertsToolbarPosition(
			this.insertsToolbarRef,
			this.entryRef,
			value
		)
	}

	onChange = ({ value }) => {
		// On slate error: throw error and force rerender to trigger ErrorBoundary
		try {
			const { value: prevValue, onChange } = this.props

			if (this.editor.current) {
				updateInsertsToolbar(
					this.insertsToolbarRef,
					this.entryRef,
					value
				)
			}

			// updateInlineToolbar(this.inlineToolbarRef, this.entryRef, value)

			onChange({ value })
		} catch (err) {
			this.setState(() => {
				throw err
			})
		}
	}

	// On title enter, jump to editors first line
	onTitleKeyDown = event => {
		if (
			event.keyCode === 13 || // Enter
			event.keyCode === 9 // Tab
		) {
			event.preventDefault()

			const editor = this.editor.current

			editor
				.moveToStartOfNode(editor.value.document.getFirstText())
				.focus()
		}
	}

	onTitleChange = event => {
		const title = event.target.value
		const { onChange } = this.props

		onChange({ title })
	}

	onFocus = (event, editor, next) => {
		clearTimeout(this.timer)

		if (next) next()

		// if (!this.state.editorHasFocus) {
		// 	this.setState({ editorHasFocus: true })
		// }
	}

	onBlur = (event, editor, next) => {
		clearTimeout(this.timer)

		const { id, lockedBy, meta, onUnlock } = this.props

		this.timer = setTimeout(() => {
			if (lockedBy && lockedBy.id === meta.userId) {
				onUnlock(id)
			}

			// if (this.state.editorHasFocus) {
			// 	this.setState({ editorHasFocus: false })
			// }
		}, 500)

		if (next) next()
	}

	render() {
		const {
			readOnly,
			title,
			value,
			id,
			draft,
			outdated,
			lockedBy,
			meta,
			isUnsaved,
			onDraftStateChange,
			onChangeOutdatedState,
			onRemove,
			onDuplicate,
			t,
			loading,
			onRate,
		} = this.props

		const { editorHasFocus, embedInputVisible, suggestionType } = this.state

		const { SuggestionPortal } = this.suggestionsPlugin
		const editorClassName = loading
			? 'o-editor o-editor--read-only'
			: this.editor.current && this.editor.current.isEmpty()
			? 'o-editor o-editor--is-empty'
			: 'o-editor'
		const lockedByOtherUser = lockedBy && lockedBy.id !== meta.userId

		return (
			<div className="o-entry__inner" ref={this.entryRef}>
				{(draft || lockedBy || outdated) && (
					<StatusInfo
						className="o-entry__status-info"
						draft={draft}
						lockedBy={lockedBy}
						outdated={outdated}
						lockedByOtherUser={lockedByOtherUser}
						onDraftStateChange={onDraftStateChange}
						onChangeOutdatedState={onChangeOutdatedState}
						meta={meta}
					/>
				)}

				<TextareaAutosize
					id="title"
					autoComplete="off"
					className={
						loading
							? 'o-entry__title o-entry__title--loading'
							: 'o-entry__title'
					}
					onChange={this.onTitleChange}
					onKeyDown={this.onTitleKeyDown}
					spellCheck={false}
					readOnly={readOnly || lockedByOtherUser || loading}
					value={title || ''}
					placeholder={t('editor.placeholder')}
					minRows={1}
					onFocus={this.onFocus}
					onBlur={this.onBlur}
				/>

				<MetaData
					meta={meta}
					lockedBy={lockedBy}
					draft={draft}
					outdated={outdated}
					value={Plain.serialize(value)}
					onDraftStateChange={onDraftStateChange}
					onChangeOutdatedState={onChangeOutdatedState}
					onRemove={onRemove}
					onDuplicate={onDuplicate}
					loading={loading}
					onRate={onRate}
				/>

				<SlateEditor
					readOnly={readOnly || lockedByOtherUser || loading}
					ref={this.editor}
					className={editorClassName}
					value={value}
					schema={schema}
					queries={queries}
					onChange={this.onChange}
					// renderNode={renderNode}
					renderMark={renderMark}
					renderBlock={renderBlock}
					renderInline={renderInline}
					spellCheck={editorHasFocus}
					plugins={this.plugins}
					commands={commands}
					// placeholder="Type your text here or use &quot;/&quot; for quick commands 👋"
					// autoFocus={false}
					// onFocus={() => {}}
					onFocus={this.onFocus}
					onBlur={this.onBlur}
				/>

				{!(readOnly || lockedByOtherUser || loading) && (
					<>
						<InlineToolbar
							ref={this.inlineToolbarRef}
							entryRef={this.entryRef}
							editor={this.editor.current}
							value={value}
						/>

						{this.editor.current &&
							!loading &&
							!embedInputVisible && (
								<InsertsToolbar
									ref={this.insertsToolbarRef}
									editor={this.editor.current}
									id={id}
									entry={this.entryRef}
								/>
							)}

						<SuggestionPortal
							value={value}
							entryRef={this.entryRef}
							editor={this.editor.current}
						/>

						{embedInputVisible && (
							<EmbedInput
								suggestionType={suggestionType}
								editor={this.editor.current}
								onSubmit={url => {
									this.setState(
										{ embedInputVisible: false },
										() => {
											try {
												quickCommandActions(
													this.editor.current,
													{ value: suggestionType },
													url
												)
											} catch (error) {
												this.setState(
													{ embedInputVisible: true },
													() => {
														alert(
															t(`editor.${error}`)
														)
													}
												)
											}
										}
									)
								}}
								onClose={() => {
									this.setState({
										embedInputVisible: false,
									})

									this.editor.current.focus()
								}}
							/>
						)}
					</>
				)}
			</div>
		)
	}
}

export default translate()(Editor)
